import React, { useState } from "react";
import {
	Dialog,
	DialogContent,
} from "@material-ui/core";
import MediaGrid from "./components/MediaGrid";
import MediaLibraryHeader from "./components/MediaLibraryHeader";
import MediaLibraryFooter from "./components/MediaLibraryFooter";
import { useMediaLibrary } from "./hooks/useMediaLibrary";
import { useMediaLibraryModalStyles } from "./utils/styles";
import { MediaFileType } from "./utils/constants";
import DeleteConfirmationModal from "../../Common/Modals/DeleteConfirmationModal";

const MediaLibraryModal = ({
	open,
	onClose,
	module,
	resources = {},
	onSelectMedia = () => {},
	dealerGroupId,
}) => {
	const classes = useMediaLibraryModalStyles();
	const [uploadModalOpen, setUploadModalOpen] = useState(false);
	const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);

	const {
		mediaFiles,
		loading,
		uploadFiles,
		copyFileUrl,
		selectionMode,
		selectedFiles,
		toggleSelectionMode,
		toggleFileSelection,
		selectAllFiles,
		deselectAllFiles,
		deleteModalOpen,
		openDeleteConfirmation,
		closeDeleteConfirmation,
		deleteSelected,
		downloadFiles,
	} = useMediaLibrary(module, resources, dealerGroupId);

	const getMediaStats = () => {
		const total = mediaFiles.length;
		const images = mediaFiles.filter(
			(file) => file.mediaFileType === MediaFileType.IMAGE
		).length;
		const documents = mediaFiles.filter(
			(file) => file.mediaFileType === MediaFileType.PDF
		).length;
		const audio = mediaFiles.filter(
			(file) => file.mediaFileType === MediaFileType.AUDIO
		).length;
		const video = mediaFiles.filter(
			(file) => file.mediaFileType === MediaFileType.VIDEO
		).length;

		const details = [];
		if (images > 0) details.push(`${images} ${resources["Images"]}`);
		if (documents > 0) details.push(`${documents} ${resources["Documents"]}`);
		if (audio > 0) details.push(`${audio} ${resources["Audio"]}`);
		if (video > 0) details.push(`${video} ${resources["Video"]}`);

		return { total, images, documents, audio, video, details };
	};

	const stats = getMediaStats();

	const handleUploadClick = () => {
		setUploadModalOpen(true);
	};

	const handleUpload = async (files) => {
		const success = await uploadFiles(files);
		if (success) {
			setUploadModalOpen(false);
		}
		return success;
	};

	const handleDownloadClick = (event) => {
		setDownloadAnchorEl(event.currentTarget);
	};

	const handleDownloadClose = () => {
		setDownloadAnchorEl(null);
	};

	const handleDownloadOriginal = () => {
		downloadFiles(selectedFiles, "original");
		handleDownloadClose();
	};

	const handleSelectAllToggle = () => {
		if (selectedFiles.length === mediaFiles.length) {
			deselectAllFiles();
		} else {
			selectAllFiles();
		}
	};

	const handleDownloadMedium = () => {
		downloadFiles(selectedFiles, "medium");
		handleDownloadClose();
	};

	return (
		<>
			<Dialog
				open={open}
				onClose={onClose}
				maxWidth={uploadModalOpen ? "md" : "lg"}
				fullWidth
				classes={{
					paper: uploadModalOpen ? classes.uploadModalRoot : classes.root,
				}}
			>
				<MediaLibraryHeader
					stats={stats}
					resources={resources}
					onClose={onClose}
				/>

				<DialogContent className={classes.content}>
					<MediaGrid
						mediaFiles={mediaFiles}
						loading={loading}
						onCopy={(url) => {
							copyFileUrl(url);
							onSelectMedia(url);
						}}
						resources={resources}
						selectionMode={selectionMode}
						selectedFiles={selectedFiles}
						onToggleSelection={toggleFileSelection}
						module={module}
						onUpload={handleUpload}
					/>
				</DialogContent>

				<MediaLibraryFooter
					selectionMode={selectionMode}
					selectedFiles={selectedFiles}
					mediaFiles={mediaFiles}
					resources={resources}
					downloadAnchorEl={downloadAnchorEl}
					handleUploadClick={handleUploadClick}
					handleSelectAllToggle={handleSelectAllToggle}
					handleDownloadClick={handleDownloadClick}
					handleDownloadClose={handleDownloadClose}
					handleDownloadOriginal={handleDownloadOriginal}
					handleDownloadMedium={handleDownloadMedium}
					openDeleteConfirmation={openDeleteConfirmation}
					toggleSelectionMode={toggleSelectionMode}
				/>
			</Dialog>

			<DeleteConfirmationModal
				open={deleteModalOpen}
				onClose={closeDeleteConfirmation}
				onConfirm={deleteSelected}
				resources={resources}
				areYouSureMessage={resources["ConfirmDeleteMedia"]}
			/>
		</>
	);
};

export default React.memo(MediaLibraryModal);
